/**
 * docHelpers — Y.Doc conversion utilities for the operations layer.
 *
 * Converts various content formats to Y.Doc for storage in `doc:Y` fields.
 * Used by addTask, setTaskDoc, onboarding, import, and API endpoints.
 *
 * **Environment compatibility:**
 * - `textToYDoc` / `jsonToYDoc` — Pure JS, works everywhere (browser, Node, CF Worker)
 * - `htmlToYDoc` — Requires DOM (browser native, Node via jsdom). Uses TipTap `generateJSON`.
 *
 * **Fragment name:** Always "default" — must match TipTap Collaboration.configure().
 */

import * as Y from "yjs"
import type { AnyExtension, JSONContent } from "@tiptap/core"

/** Y.Doc XmlFragment name used by TipTap Collaboration. */
const FRAGMENT_NAME = "default"

// ---------------------------------------------------------------------------
// DI: Schema + extensions (set once per environment)
// ---------------------------------------------------------------------------

let _extensions: AnyExtension[] | null = null
let _schema: unknown = null
let _generateJSON: ((html: string, extensions: AnyExtension[]) => JSONContent) | null = null
let _generateHTML: ((doc: JSONContent, extensions: AnyExtension[]) => string) | null = null

export interface DocContext {
  /** TipTap extension array (headless set) */
  extensions: AnyExtension[]
  /** ProseMirror schema derived from extensions (via getSchema()) */
  schema: unknown
  /** TipTap's generateJSON — HTML → ProseMirror JSON (needs DOM) */
  generateJSON: (html: string, extensions: AnyExtension[]) => JSONContent
  /** TipTap's generateHTML — ProseMirror JSON → HTML string */
  generateHTML: (doc: JSONContent, extensions: AnyExtension[]) => string
}

/**
 * Set the document context for doc conversion operations.
 * Call once at startup — after TipTap imports are available.
 *
 * @example
 * // Client (src/db/schema.ts)
 * import { setDocContext } from "@totodo/operations"
 * import { generateJSON, generateHTML, getSchema } from "@tiptap/core"
 * import { headlessExtensions } from "@/services/doc-processing/headlessExtensions"
 * setDocContext({ extensions: headlessExtensions, schema: getSchema(headlessExtensions), generateJSON, generateHTML })
 *
 * // Server (server/src/db/operations/index.ts) — with jsdom for DOM
 * import { JSDOM } from "jsdom"
 * const { window } = new JSDOM()
 * globalThis.window ??= window; globalThis.document ??= window.document
 * // Then same as client
 */
export function setDocContext(ctx: DocContext): void {
  _extensions = ctx.extensions
  _schema = ctx.schema
  _generateJSON = ctx.generateJSON
  _generateHTML = ctx.generateHTML
}

let _loader: (() => Promise<DocContext>) | null = null
let _loading: Promise<void> | null = null

/**
 * Register a loader that builds the DocContext on first use instead of `setDocContext()` at
 * startup. For environments where the editor stack (TipTap, its extensions, a DOM) is heavy and
 * most requests never touch a document (the CF Worker / Durable Object isolate shares 128 MB
 * between several users): `hasDocContext()` is true from here on, and every async converter below
 * awaits `ensureDocContext()` before it reads the context. An explicit `setDocContext()` wins.
 */
export function setDocContextLoader(loader: () => Promise<DocContext>): void {
  _loader = loader
}

/**
 * Make sure the DocContext is set: run the loader registered with `setDocContextLoader` once (a
 * failed load is not remembered, the next call tries again). A no-op when the context is already
 * set. Call it before `getDocContext()` / `htmlToJson()` in async code.
 */
export async function ensureDocContext(): Promise<void> {
  if (_extensions) return
  if (!_loader) return // getDocContext() reports "not configured"
  _loading ??= _loader()
    .then((ctx) => {
      if (!_extensions) setDocContext(ctx)
    })
    .finally(() => {
      _loading = null
    })
  await _loading
}

export function getDocContext(): DocContext {
  if (!_extensions || !_schema || !_generateJSON || !_generateHTML) {
    throw new Error(
      _loader
        ? "DocContext not loaded yet. await ensureDocContext() first."
        : "DocContext not configured. Call setDocContext() at startup.",
    )
  }
  return {
    extensions: _extensions,
    schema: _schema,
    generateJSON: _generateJSON,
    generateHTML: _generateHTML,
  }
}

/** Check if DocContext has been configured (set, or registered to load on first use). */
export function hasDocContext(): boolean {
  return (_extensions !== null && _schema !== null) || _loader !== null
}

// ---------------------------------------------------------------------------
// Core converters
// ---------------------------------------------------------------------------

// Dynamic import for y-tiptap (to avoid top-level import issues in some envs)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _yTiptap: typeof import("@tiptap/y-tiptap") | null = null

async function getYTiptap() {
  if (!_yTiptap) {
    _yTiptap = await import("@tiptap/y-tiptap")
  }
  return _yTiptap
}

/**
 * Convert ProseMirror JSON to Y.Doc.
 *
 * Pure JS — no DOM needed. Works in all environments.
 *
 * @param json - ProseMirror JSON document (type: "doc", content: [...])
 * @returns Y.Doc with content in the "default" fragment
 */
export async function jsonToYDoc(json: JSONContent): Promise<Y.Doc> {
  await ensureDocContext()
  const { schema } = getDocContext()
  const { prosemirrorJSONToYDoc } = await getYTiptap()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return prosemirrorJSONToYDoc(schema as any, json, FRAGMENT_NAME)
}

/**
 * Convert plain text to Y.Doc.
 *
 * Each line becomes a paragraph node.
 * Pure JS — no DOM needed. Works in all environments.
 */
export async function textToYDoc(text: string): Promise<Y.Doc> {
  const json = textToJson(text)
  return jsonToYDoc(json)
}

/**
 * Convert HTML string to Y.Doc.
 *
 * Requires DOM (browser or jsdom). Uses TipTap's generateJSON internally.
 *
 * @param html - HTML string, e.g. "<p>Hello <strong>world</strong></p>"
 */
export async function htmlToYDoc(html: string): Promise<Y.Doc> {
  await ensureDocContext()
  const json = htmlToJson(html)
  return jsonToYDoc(json)
}

/**
 * Convert Y.Doc to HTML string.
 *
 * Uses TipTap's generateHTML with the headless extension set.
 */
export async function yDocToHtml(doc: Y.Doc): Promise<string> {
  await ensureDocContext()
  const { extensions, generateHTML } = getDocContext()
  const { yDocToProsemirrorJSON } = await getYTiptap()
  const json = yDocToProsemirrorJSON(doc, FRAGMENT_NAME)
  return generateHTML(json, extensions)
}

/**
 * Convert Y.Doc to binary Uint8Array (Yjs state update).
 *
 * Useful for storage/transport without full Y.Doc materialization.
 */
export function yDocToUpdate(doc: Y.Doc): Uint8Array {
  return Y.encodeStateAsUpdate(doc)
}

// ---------------------------------------------------------------------------
// Format detection + unified conversion
// ---------------------------------------------------------------------------

export type DocFormat = "text" | "html" | "json" | "markdown"

/** Elements an HTML document fragment (a TipTap export, an AI answer in HTML) starts with. */
const HTML_ROOT_TAGS =
  "p|div|span|h[1-6]|ul|ol|li|blockquote|pre|code|table|thead|tbody|tr|td|th|hr|br|img|a|strong|em|b|i|u|s|mark|section|article|details|summary|figure|label|input|html|body"
const HTML_FRAGMENT_START = new RegExp(String.raw`^(?:<!doctype html[^>]*>\s*)?<(?:${HTML_ROOT_TAGS})(?:[\s/>])`, "i")

/** Is `trimmed` the text of a ProseMirror document (not just something that starts with a brace)? */
function isProseMirrorJson(trimmed: string): boolean {
  if (!trimmed.startsWith("{") || !trimmed.includes('"type"')) return false
  try {
    const value = JSON.parse(trimmed) as { type?: unknown } | null
    return value !== null && typeof value === "object" && value.type === "doc"
  } catch {
    return false
  }
}

/**
 * Is `trimmed` clearly an HTML document fragment: it starts with a block / inline element and ends
 * with a tag, and no line of it is a Markdown heading or a code fence? Anything else that merely
 * contains something tag-like ("Use <details> in the page", "List<string>", Markdown with an
 * inline <br>) is not HTML: the HTML path drops what it does not know, the Markdown / text path
 * keeps every character.
 */
function isHtmlFragment(trimmed: string): boolean {
  if (!HTML_FRAGMENT_START.test(trimmed) || !trimmed.endsWith(">")) return false
  return !/^(#{1,6}\s|```|~~~)/m.test(trimmed)
}

/**
 * Auto-detect content format from a string. Conservative: when in doubt the content is Markdown or
 * plain text, which keep what was written; pass `format` to force another reading.
 *
 * - A ProseMirror document (valid JSON with `"type": "doc"`) → json
 * - A clear HTML fragment (see isHtmlFragment) → html
 * - Contains Markdown indicators (headings, bold, lists, blockquotes,
 *   code fences, ordered lists, links, horizontal rules) → markdown
 * - Otherwise → text
 */
export function detectFormat(content: string): DocFormat {
  const trimmed = content.trim()
  if (isProseMirrorJson(trimmed)) return "json"
  if (isHtmlFragment(trimmed)) return "html"
  if (/^#{1,6}\s|^\*\*|^[-*+]\s|\n[-*+]\s|^>\s|\n>\s|^```|^\d+\.\s|\[.*\]\(.*\)|^[-*_]{3,}\s*$/m.test(trimmed)) return "markdown"
  return "text"
}

/**
 * Convert content in any supported format to Y.Doc.
 *
 * @param content - The content string
 * @param format - Optional format hint. Auto-detected if omitted.
 */
export async function contentToYDoc(
  content: string,
  format?: DocFormat,
): Promise<Y.Doc> {
  const fmt = format ?? detectFormat(content)
  switch (fmt) {
    case "json":
      return jsonToYDoc(JSON.parse(content) as JSONContent)
    case "html":
      return htmlToYDoc(content)
    case "markdown":
      return htmlToYDoc(markdownToHtmlMd(content))
    case "text":
      return textToYDoc(content)
  }
}

/**
 * Convert content in any supported format to Y.Doc + HTML pair.
 *
 * Returns both the Y.Doc (for Dexie storage) and HTML (for search/preview).
 */
export async function contentToDocPair(
  content: string,
  format?: DocFormat,
): Promise<{ doc: Y.Doc; docHtml: string }> {
  const doc = await contentToYDoc(content, format)
  const docHtml = await yDocToHtml(doc)
  return { doc, docHtml }
}

// ---------------------------------------------------------------------------
// JSON construction helpers (pure JS, no DOM)
// ---------------------------------------------------------------------------

/** Convert plain text to ProseMirror JSON. Each line → paragraph. */
export function textToJson(text: string): JSONContent {
  const lines = text.split("\n")
  const content: JSONContent[] = lines.map((line) => {
    if (!line.trim()) {
      return { type: "paragraph" }
    }
    return {
      type: "paragraph",
      content: [{ type: "text", text: line }],
    }
  })
  return { type: "doc", content }
}

/**
 * Convert HTML string to ProseMirror JSON via TipTap generateJSON. Needs DOM.
 * Synchronous: with a lazily loaded context, `await ensureDocContext()` first.
 */
export function htmlToJson(html: string): JSONContent {
  const { extensions, generateJSON } = getDocContext()
  return generateJSON(html, extensions)
}

// ---------------------------------------------------------------------------
// Markdown → HTML conversion (markdown-it based, AST-parsed)
// ---------------------------------------------------------------------------

import { markdownToHtmlMd, looksLikeMarkdown } from "./markdownParser.js"
export { looksLikeMarkdown }
export { markdownToHtmlMd as markdownToHtml }
